import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryGiftCardData = Content.Components['GiftCardModuleDto'];

interface UseQueryGiftCardOptions {
  enabled?: boolean;
  initialData?: UseQueryGiftCardData;
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
}

const useQueryGiftCard = ({
  enabled = true,
  initialData,
  parameters,
}: UseQueryGiftCardOptions) => {
  const locale = getLocale();

  const query = useQuery<UseQueryGiftCardData>(
    [QueryId.GIFT_CARD, parameters, locale],
    async () => {
      const baseUrl = '/api/content-modules/gift-card';
      const queryParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${queryParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryGiftCard', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!parameters && enabled,
      initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryGiftCard;
